import { useEffect, useMemo, useState } from "react";
import CollapsibleSection from "./CollapsibleSection";
import ColumnFilterGrid from "./ColumnFilterGrid";
import { api } from "../api/client";
import { useToast } from "./Toast";
import type { FrameRecords, SideDisplayConfig } from "../types/api";

const SIG = "unrec_sig";
// What settled each item, beside the item's own columns. Same names the Items view uses.
const EVIDENCE: Record<string, string> = {
  unrec_matched_in: "Settled on",
  unrec_cleared_by_tier: "Matched by",
  unrec_cleared_by_ref: "Settled by reference",
  unrec_cleared_by_amount: "Settled by amount",
};

// The rows of `frame` passing `keep`, as a frame of their own.
function slice(frame: FrameRecords, keep: (r: FrameRecords["rows"][number]) => boolean): FrameRecords {
  const rows: FrameRecords["rows"] = [];
  const indices: FrameRecords["indices"] = [];
  frame.rows.forEach((r, i) => {
    if (!keep(r)) return;
    rows.push(r);
    indices.push(frame.indices[i] ?? i);
  });
  return { columns: frame.columns, rows, indices, total_rows: rows.length };
}

// Prior-period Manual Match's Release equivalent. An AUTOMATIC clear finance disagrees
// with is REJECTED — the item is held open whatever the ladder concludes — and a rejection
// is RESTORED to let the ladder clear it again. Both persist per insurer against the
// item's content signature and are reversible; neither touches the reconciliation itself.
// Manual pairs are not listed: those are undone by Forget in the remembered list.
export default function CarriedClearReview({ rows, config, book, insurerName, onChanged }: {
  rows: FrameRecords;
  config: SideDisplayConfig;
  book: "insurer" | "cri";
  insurerName: string;
  onChanged?: () => void;
}) {
  const toast = useToast();
  const col = (name: string) => rows.columns.indexOf(name);
  const [mi, xi, ri, ti] = [col("unrec_matched"), col("unrec_excluded"),
                            col("unrec_rejected"), col("unrec_cleared_by_tier")];
  const autoClears = useMemo(() => slice(rows, (r) =>
    mi >= 0 && Boolean(r[mi]) && !(xi >= 0 && r[xi]) && !(ti >= 0 && r[ti] === "Manual")),
    [rows, mi, xi, ti]);
  const rejected = useMemo(() => slice(rows, (r) => ri >= 0 && Boolean(r[ri])), [rows, ri]);
  const clearsConfig: SideDisplayConfig = useMemo(() => ({
    columns: [...(config.columns ?? []), ...Object.keys(EVIDENCE).filter((c) => rows.columns.includes(c))],
    renames: { ...(config.renames ?? {}), ...EVIDENCE },
  }), [config, rows.columns]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [toReject, setToReject] = useState<Set<string>>(new Set());
  const [toRestore, setToRestore] = useState<Set<string>>(new Set());
  const [shownClears, setShownClears] = useState<string[] | null>(null);
  const [shownRejected, setShownRejected] = useState<string[] | null>(null);
  // A re-run moves items between the two lists; a tick on a row that left is dropped.
  useEffect(() => { setToReject(new Set()); setToRestore(new Set()); }, [rows]);

  const picker = (setter: typeof setToReject) => ({
    one: (s: string | null) => s && setter((p) => {
      const n = new Set(p);
      if (n.has(s)) n.delete(s); else n.add(s);
      return n;
    }),
    many: (sigs: string[], on: boolean) => setter((p) => {
      const n = new Set(p);
      for (const s of sigs) { if (on) n.add(s); else n.delete(s); }
      return n;
    }),
  });
  const rej = picker(setToReject);
  const res = picker(setToRestore);
  // Only what the search and filters leave on screen is acted on.
  const scoped = (picked: Set<string>, shown: string[] | null) =>
    [...picked].filter((s) => !shown || shown.includes(s));

  async function apply(sigs: string[], reject: boolean) {
    if (!sigs.length) return;
    setBusy(true);
    setError("");
    let done = 0;
    try {
      for (const sig of sigs) {
        if (reject) await api.rejectUnreconciledClear(insurerName, sig, "", book);
        else await api.restoreUnreconciledClear(insurerName, sig, book);
        done++;
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
    if (done) {
      toast.success(`${done} item${done === 1 ? "" : "s"} ${reject ? "held open" : "restored"} — recalculating.`);
      // The engine applies the override in the finalize tail, so the figures move only
      // once the reconciliation re-runs.
      onChanged?.();
    }
  }

  const rejectSigs = scoped(toReject, shownClears);
  const restoreSigs = scoped(toRestore, shownRejected);
  const list = book === "cri" ? "CRI" : "insurer";
  return (
    <div style={{ marginTop: 16 }}>
      {error && <div className="badge badge-risk" style={{ display: "block", marginBottom: 8 }}>{error}</div>}
      <CollapsibleSection title={`Automatic clears on the ${list} list`}
        sub={`${autoClears.total_rows} item${autoClears.total_rows === 1 ? "" : "s"}`}
        hint="Items the app cleared on its own. Reject one it got wrong to hold it open; nothing in the reconciliation changes, and Restore undoes it.">
        {autoClears.total_rows === 0 ? (
          <p className="muted">The app cleared no item on this list automatically.</p>
        ) : (
          <>
            <div className="msearch-actions">
              <button className="btn" disabled={busy || rejectSigs.length === 0}
                onClick={() => apply(rejectSigs, true)}>
                {busy ? "…" : `Reject ${rejectSigs.length} selected`}
              </button>
              <span className="muted">Paste references into the search to find the clears to check.</span>
            </div>
            <ColumnFilterGrid frame={autoClears} sigColumn={SIG}
              selectedSig={null} selectedSigs={toReject} onSelect={rej.one}
              onSelectMany={rej.many} onFilteredChange={setShownClears}
              config={clearsConfig} resetKey={`${insurerName}|clears|${book}`} compact
              pinSide={book} insurerName={insurerName} title="Automatic clears" />
          </>
        )}
      </CollapsibleSection>
      {rejected.total_rows > 0 && (
        <CollapsibleSection title={`Rejected clears on the ${list} list`} defaultOpen
          sub={`${rejected.total_rows} item${rejected.total_rows === 1 ? "" : "s"} held open`}>
          <div className="msearch-actions">
            <button className="btn" disabled={busy || restoreSigs.length === 0}
              onClick={() => apply(restoreSigs, false)}>
              {busy ? "…" : `Restore ${restoreSigs.length} selected`}
            </button>
          </div>
          <ColumnFilterGrid frame={rejected} sigColumn={SIG}
            selectedSig={null} selectedSigs={toRestore} onSelect={res.one}
            onSelectMany={res.many} onFilteredChange={setShownRejected}
            config={config} resetKey={`${insurerName}|rejected|${book}`} compact
            pinSide={book} insurerName={insurerName} title="Rejected clears" />
        </CollapsibleSection>
      )}
    </div>
  );
}
